// AgencyLoginPage.js - Login page for agency agents with 2FA
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { authService } from '../../services';
import { useAuth } from '../../contexts/AuthContext';

const AgencyLoginPage = ({ agency, agencyCode, color = 'blue' }) => {
  const navigate = useNavigate();
  const { login: authLogin, checkAuth } = useAuth();
  const [isLogin, setIsLogin] = useState(true);
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  
  // 2FA states
  const [show2FA, setShow2FA] = useState(false);
  const [verificationMethod, setVerificationMethod] = useState('email');
  const [verificationCode, setVerificationCode] = useState('');
  const [tempToken, setTempToken] = useState('');
  const [resendTimer, setResendTimer] = useState(0);
  const [userContact, setUserContact] = useState({ email: '', phone: '' });
  const [codeSent, setCodeSent] = useState(false);

  const getColorClasses = () => {
    const colorMap = {
      blue: {
        bg: 'bg-blue-600 hover:bg-blue-700',
        focus: 'focus:ring-blue-500 focus:border-blue-500',
        gradient: 'from-blue-50 to-blue-100',
        text: 'text-blue-600'
      },
      green: {
        bg: 'bg-green-600 hover:bg-green-700',
        focus: 'focus:ring-green-500 focus:border-green-500',
        gradient: 'from-green-50 to-green-100',
        text: 'text-green-600'
      },
      purple: {
        bg: 'bg-purple-600 hover:bg-purple-700',
        focus: 'focus:ring-purple-500 focus:border-purple-500',
        gradient: 'from-purple-50 to-purple-100',
        text: 'text-purple-600'
      }
    };
    return colorMap[color] || colorMap.blue;
  };

  const colors = getColorClasses();

  // Start resend timer
  const startResendTimer = () => {
    setResendTimer(60);
    const interval = setInterval(() => {
      setResendTimer((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (isLogin) {
        // Step 1: Initial login attempt
        const response = await authService.login({
          email: formData.email,
          password: formData.password,
          agencyCode: agencyCode
        });
        
        // If 2FA is required, the backend will return a temp token
        if (response.data.requires2FA) {
          setTempToken(response.data.tempToken);
          setUserContact({
            email: response.data.maskedEmail || formData.email,
            phone: response.data.maskedPhone || ''
          });
          setShow2FA(true);
          setLoading(false);
          
          // Don't send initial 2FA code automatically - let user choose method first
        } else {
          // No 2FA required, proceed with login
          await handleLoginSuccess(response.data);
        }
      } else {
        // Redirect to agency registration
        navigate(`/${agencyCode.toLowerCase()}/register`);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Invalid credentials');
      setLoading(false);
    }
  };

  const send2FACode = async (selectedMethod = verificationMethod) => {
    try {
      await authService.send2FACode({
        tempToken,
        method: selectedMethod
      });
      setCodeSent(true);
      startResendTimer();
    } catch (err) {
      setError('Failed to send verification code');
    }
  };

  const handleVerify2FA = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await authService.verify2FA({
        tempToken,
        code: verificationCode
      });

      // Update the auth context with the new user data
      await checkAuth();

      // Navigate based on user role
      const userData = response.data.user;
      if (userData.role === 'agency_admin') {
        navigate('/agency/dashboard');
      } else if (userData.role === 'agent') {
        navigate('/agent/dashboard');
      } else if (userData.role === 'super_admin') {
        navigate('/admin/dashboard');
      } else {
        navigate('/agent/dashboard'); // Default to agent dashboard
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Invalid verification code');
    } finally {
      setLoading(false);
    }
  };

  const handleLoginSuccess = async (data) => {
    // Store auth data properly
    if (data.token) {
      localStorage.setItem('token', data.token);
      localStorage.setItem('user', JSON.stringify(data.user));
    }
    
    // Use authLogin from context
    authLogin(data);
    
    // Add a small delay to ensure state updates and prevent navigation issues
    setTimeout(() => {
//       // Tax form redirect removed - now handled during registration
      
      // Navigate based on user role
      if (data.user.role === 'agency_admin') {
        navigate('/agency/dashboard');
      } else if (data.user.role === 'agent') {
        navigate('/agent/dashboard');
      } else if (data.user.role === 'super_admin') {
        navigate('/admin/dashboard');
      } else {
        navigate('/agent/dashboard'); // Default to agent dashboard
      }
    }, 100);
  };

  const resend2FA = async () => {
    if (resendTimer > 0) return;
    
    try {
      await send2FACode();
      setError('');
    } catch (err) {
      setError('Failed to resend code');
    }
  };

  if (show2FA) {
    return (
      <div className={`min-h-screen flex items-center justify-center bg-gradient-to-br ${colors.gradient} py-12 px-4 sm:px-6 lg:px-8`}>
        <div className="max-w-md w-full space-y-8">
          <div>
            <div className="text-center">
              <div className={`mx-auto h-20 w-20 ${colors.bg} rounded-full flex items-center justify-center mb-4`}>
                <span className="text-3xl font-bold text-white">{agencyCode}</span>
              </div>
              <h2 className="text-3xl font-extrabold text-gray-900">
                Two-Factor Authentication
              </h2>
              <p className="mt-2 text-sm text-gray-600">
                {!codeSent ? 
                  'Choose how you would like to receive your verification code' :
                  `We've sent a verification code to your ${verificationMethod}`
                }
              </p>
            </div>
          </div>

          <div className="bg-white py-8 px-4 shadow-xl rounded-lg sm:px-10">
            {error && (
              <div className="mb-4 bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded">
                {error}
              </div>
            )}

            <form onSubmit={handleVerify2FA} className="space-y-6">
              <div className="text-center mb-6">
                <div className="flex justify-center space-x-4 mb-4">
                  <button
                    type="button"
                    onClick={async () => {
                      if (verificationMethod !== 'email') {
                        // Just switch the method
                        setVerificationMethod('email');
                      }
                      
                      if (!codeSent) {
                        // Send code if not already sent
                        try {
                          await send2FACode('email');
                          setError('');
                        } catch (err) {
                          setError('Failed to send verification code');
                        }
                      }
                    }}
                    className={`px-4 py-2 rounded-md text-sm font-medium ${
                      verificationMethod === 'email'
                        ? `${colors.bg} text-white`
                        : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                    }`}
                    disabled={loading}
                  >
                    Email
                  </button>
                  {userContact.phone && (
                    <button
                      type="button"
                      onClick={async () => {
                        if (verificationMethod !== 'sms') {
                          // Just switch the method
                          setVerificationMethod('sms');
                        }
                        
                        if (!codeSent) {
                          // Send code if not already sent
                          try {
                            await send2FACode('sms');
                            setError('');
                          } catch (err) {
                            setError('Failed to send verification code');
                          }
                        }
                      }}
                      className={`px-4 py-2 rounded-md text-sm font-medium ${
                        verificationMethod === 'sms'
                          ? `${colors.bg} text-white`
                          : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                      }`}
                      disabled={loading}
                    >
                      SMS
                    </button>
                  )}
                </div>
                <p className="text-sm text-gray-600">
                  {verificationMethod === 'email' 
                    ? `Code sent to: ${userContact.email}`
                    : `Code sent to: ${userContact.phone}`
                  }
                </p>
              </div>

              <div>
                <label htmlFor="code" className="block text-sm font-medium text-gray-700">
                  Verification Code
                </label>
                <input
                  id="code"
                  name="code"
                  type="text"
                  autoComplete="one-time-code"
                  required
                  maxLength="6"
                  className={`mt-1 appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none ${colors.focus} sm:text-sm text-center text-2xl tracking-widest`}
                  value={verificationCode}
                  onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, ''))}
                  placeholder="000000"
                />
              </div>

              <div className="text-center">
                <button
                  type="button"
                  onClick={resend2FA}
                  disabled={resendTimer > 0}
                  className={`text-sm ${resendTimer > 0 ? 'text-gray-400' : colors.text + ' hover:opacity-80'}`}
                >
                  {resendTimer > 0 
                    ? `Resend code in ${resendTimer}s`
                    : 'Resend code'
                  }
                </button>
              </div>

              <div>
                <button
                  type="submit"
                  disabled={loading || verificationCode.length !== 6}
                  className={`w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white ${colors.bg} focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-${color}-500 disabled:opacity-50`}
                >
                  {loading ? 'Verifying...' : 'Verify Code'}
                </button>
              </div>

              <div className="text-center">
                <button
                  type="button"
                  onClick={() => {
                    setShow2FA(false);
                    setVerificationCode('');
                    setError('');
                    setCodeSent(false);
                  }}
                  className="text-sm text-gray-600 hover:text-gray-900"
                >
                  ← Back to login
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen flex items-center justify-center bg-gradient-to-br ${colors.gradient} py-12 px-4 sm:px-6 lg:px-8`}>
      <div className="max-w-md w-full space-y-8">
        <div>
          <div className="text-center">
            <div className={`mx-auto h-20 w-20 ${colors.bg} rounded-full flex items-center justify-center mb-4`}>
              <span className="text-3xl font-bold text-white">{agencyCode}</span>
            </div>
            <h2 className="text-3xl font-extrabold text-gray-900">
              {agency} Agent Portal
            </h2>
            <p className="mt-2 text-sm text-gray-600">
              {isLogin ? 'Sign in to access your dashboard' : 'Apply to join our agency'}
            </p>
          </div>
        </div>

        <div className="bg-white py-8 px-4 shadow-xl rounded-lg sm:px-10">
          {error && (
            <div className="mb-4 bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded">
              {error}
            </div>
          )}

          {isLogin ? (
            <form className="space-y-6" onSubmit={handleSubmit}>
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                  Email address
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  className={`mt-1 appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none ${colors.focus} sm:text-sm`}
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                />
              </div>

              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                  Password
                </label>
                <div className="mt-1 relative">
                  <input
                    id="password"
                    name="password"
                    type={showPassword ? "text" : "password"}
                    autoComplete="current-password"
                    required
                    className={`appearance-none block w-full px-3 py-2 pr-10 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none ${colors.focus} sm:text-sm`}
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    <svg className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      {showPassword ? (
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                      ) : (
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      )}
                    </svg>
                  </button>
                </div>
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <input
                    id="remember-me"
                    name="remember-me"
                    type="checkbox"
                    className={`h-4 w-4 ${colors.text} focus:ring-${color}-500 border-gray-300 rounded`}
                  />
                  <label htmlFor="remember-me" className="ml-2 block text-sm text-gray-900">
                    Remember me
                  </label>
                </div>

                <div className="text-sm">
                  <a href="#" className={`font-medium ${colors.text} hover:opacity-80`}>
                    Forgot your password?
                  </a>
                </div>
              </div>

              <div className="text-xs text-gray-600 text-center">
                Two-factor authentication is enabled for your security
              </div>

              <div>
                <button
                  type="submit"
                  disabled={loading}
                  className={`w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white ${colors.bg} focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-${color}-500 disabled:opacity-50`}
                >
                  {loading ? 'Signing in...' : 'Sign in'}
                </button>
              </div>
            </form>
          ) : (
            <div className="text-center">
              <p className="mb-6 text-gray-600">
                To apply to join {agency}, please click the button below to access our application form.
              </p>
              <button
                onClick={() => window.location.href = `/${agencyCode.toLowerCase()}/register`}
                className={`w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white ${colors.bg} focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-${color}-500`}
              >
                Apply to Join {agency}
              </button>
            </div>
          )}

          <div className="mt-6">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-white text-gray-500">
                  {isLogin ? "New to " + agency + "?" : "Already an agent?"}
                </span>
              </div>
            </div>

            <div className="mt-6">
              <button
                onClick={() => {
                  setIsLogin(!isLogin);
                  setError('');
                }}
                className="w-full inline-flex justify-center py-2 px-4 border border-gray-300 rounded-md shadow-sm bg-white text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                {isLogin ? 'Apply to join ' + agency : 'Sign in instead'}
              </button>
            </div>
          </div>
        </div>

        <div className="text-center space-y-2">
          <div>
            <a href={`/${agencyCode.toLowerCase()}/admin`} className={`text-sm ${colors.text} hover:opacity-80`}>
              Agency Admin Login →
            </a>
          </div>
          <div>
            <a href="/" className="text-sm text-gray-600 hover:text-gray-900">
              ← Back to home
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AgencyLoginPage;